/**
 * Push Notification Manager Component
 * Handles Web Push subscription and preferences
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Bell, BellOff, BellRing, Smartphone, Mail, 
  FileText, Calendar, CreditCard, AlertTriangle, Settings,
  CheckCircle, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationManager() {
  const { token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    new_quotes: true,
    new_documents: true,
    meeting_convocations: true,
    payment_approved: true,
    payment_due_reminder: true,
    system_alerts: true,
    email_backup: true
  });

  // Check if push is supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check subscription status and load preferences
  useEffect(() => {
    if (!token || !isSupported) {
      setIsLoading(false);
      return;
    }

    const checkStatus = async () => {
      try {
        // Check local subscription
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);

        // Load preferences from server
        const res = await fetch(`${API_URL}/api/push/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const prefs = await res.json();
          setPreferences(prev => ({ ...prev, ...prefs }));
        }
      } catch (error) {
        console.error('Error checking push status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [token, isSupported]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;

    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    if (!token) return;

    setIsSaving(true);
    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast.error('Permesso notifiche non concesso');
        return;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        toast.error('Errore registrazione Service Worker');
        return;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidRes = await fetch(`${API_URL}/api/push/vapid-public-key`);
      if (!vapidRes.ok) throw new Error('Failed to get VAPID key');
      const { publicKey } = await vapidRes.json();

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const subRes = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
        })
      });

      if (!subRes.ok) throw new Error('Failed to save subscription');

      setIsSubscribed(true);
      toast.success('Notifiche push attivate!');
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Errore attivazione notifiche');
    } finally {
      setIsSaving(false);
    }
  }, [token, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribeFromPush = useCallback(async () => {
    if (!token) return;

    setIsSaving(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();

        // Notify server
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {}
          })
        });
      }

      setIsSubscribed(false);
      toast.success('Notifiche push disattivate');
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      toast.error('Errore disattivazione notifiche');
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  // Update preferences
  const updatePreferences = useCallback(async (newPrefs) => {
    if (!token) return;

    setPreferences(newPrefs);
    
    try {
      const res = await fetch(`${API_URL}/api/push/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPrefs)
      });

      if (!res.ok) throw new Error('Failed to update preferences');
      toast.success('Preferenze salvate');
    } catch (error) {
      console.error('Preferences update error:', error);
      toast.error('Errore salvataggio preferenze');
    }
  }, [token]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Test failed');
      toast.success('Notifica di test inviata!');
    } catch (error) {
      toast.error('Errore invio notifica test');
    }
  }, [token]);

  // Handle preference toggle
  const handlePrefToggle = (key) => {
    const newPrefs = { ...preferences, [key]: !preferences[key] };
    updatePreferences(newPrefs);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!isSupported) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Il tuo browser non supporta le notifiche push. 
          Prova con Chrome, Firefox, Edge o Safari su macOS.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isSubscribed ? (
                <div className="p-2 rounded-full bg-green-100">
                  <BellRing className="h-5 w-5 text-green-600" />
                </div>
              ) : (
                <div className="p-2 rounded-full bg-gray-100">
                  <BellOff className="h-5 w-5 text-gray-500" />
                </div>
              )}
              <div>
                <CardTitle>Notifiche Push</CardTitle>
                <CardDescription>
                  Ricevi notifiche in tempo reale sul tuo dispositivo
                </CardDescription>
              </div>
            </div>
            <Badge variant={isSubscribed ? "default" : "secondary"}>
              {isSubscribed ? "Attive" : "Disattivate"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {permission === 'denied' && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                Le notifiche sono state bloccate. Modifica le impostazioni del browser per abilitarle.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Questo dispositivo</span>
            </div>
            <div className="flex gap-2">
              {isSubscribed ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendTestNotification}
                  >
                    Test
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={unsubscribeFromPush}
                    disabled={isSaving}
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disattiva'}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={subscribeToPush}
                  disabled={isSaving || permission === 'denied'}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Bell className="h-4 w-4 mr-2" />
                  )}
                  Attiva Notifiche
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle>Preferenze Notifiche</CardTitle>
          </div>
          <CardDescription>
            Scegli quali notifiche ricevere
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quote Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-blue-500" />
              <Label htmlFor="new_quotes" className="cursor-pointer">
                Nuove quote mensili
              </Label>
            </div>
            <Switch
              id="new_quotes"
              checked={preferences.new_quotes}
              onCheckedChange={() => handlePrefToggle('new_quotes')}
            />
          </div>

          {/* Document Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-green-500" />
              <Label htmlFor="new_documents" className="cursor-pointer">
                Nuovi documenti
              </Label>
            </div>
            <Switch
              id="new_documents"
              checked={preferences.new_documents}
              onCheckedChange={() => handlePrefToggle('new_documents')}
            />
          </div>

          {/* Meeting Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-purple-500" />
              <Label htmlFor="meeting_convocations" className="cursor-pointer">
                Convocazioni assemblea
              </Label>
            </div>
            <Switch
              id="meeting_convocations"
              checked={preferences.meeting_convocations}
              onCheckedChange={() => handlePrefToggle('meeting_convocations')}
            />
          </div>

          {/* Payment Approved */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <Label htmlFor="payment_approved" className="cursor-pointer">
                Pagamento approvato
              </Label>
            </div>
            <Switch
              id="payment_approved"
              checked={preferences.payment_approved}
              onCheckedChange={() => handlePrefToggle('payment_approved')}
            />
          </div>

          {/* Payment Due */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="h-4 w-4 text-orange-500" />
              <Label htmlFor="payment_due_reminder" className="cursor-pointer">
                Scadenza pagamenti
              </Label>
            </div>
            <Switch
              id="payment_due_reminder"
              checked={preferences.payment_due_reminder}
              onCheckedChange={() => handlePrefToggle('payment_due_reminder')}
            />
          </div>

          {/* System Alerts */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <Label htmlFor="system_alerts" className="cursor-pointer">
                Avvisi di sistema e disservizi
              </Label>
            </div>
            <Switch
              id="system_alerts"
              checked={preferences.system_alerts}
              onCheckedChange={() => handlePrefToggle('system_alerts')}
            />
          </div>

          <hr className="my-4" />

          {/* Email Backup */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-gray-500" />
              <div>
                <Label htmlFor="email_backup" className="cursor-pointer">
                  Backup via email
                </Label>
                <p className="text-xs text-muted-foreground">
                  Ricevi email se le push non sono disponibili
                </p>
              </div>
            </div>
            <Switch
              id="email_backup"
              checked={preferences.email_backup}
              onCheckedChange={() => handlePrefToggle('email_backup')}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
